import React, { useMemo, useState } from 'react';
import { Button, Card, Modal, Select, Table, Typography, Tooltip } from 'antd';
import { useGetAllPoolsQuery, useGetMembersLotterySummaryQuery, MembersLotterySummaryResponse } from './lotteryAPI';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';

const { Text } = Typography;

interface TableRow {
  key: string;
  memberId: string;
  memberName: string;
  totalTickets: number;
  // dynamic category counts added at runtime
  [categoryKey: string]: any;
}

const MembersLotteryReport: React.FC = () => {
  const { data: pools } = useGetAllPoolsQuery();
  const [poolId, setPoolId] = useState<number | undefined>(undefined);
  const { data: summary, isFetching, error } = useGetMembersLotterySummaryQuery(poolId);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalTickets, setModalTickets] = useState<string[]>([]);

  const columns = useMemo(() => {
    const baseCols: any[] = [
      {
        title: 'Anggota',
        key: 'memberInfo',
        sorter: (a: TableRow, b: TableRow) => {
          const aKey = `${a.memberName} ${a.memberId}`.trim();
          const bKey = `${b.memberName} ${b.memberId}`.trim();
          return aKey.localeCompare(bKey);
        },
        render: (_: unknown, row: TableRow) => (
          <div>
            <div>{row.memberName}</div>
            <Text type="secondary">{row.memberId}</Text>
          </div>
        ),
      },
    ];

    if (!summary) {
      return baseCols;
    }

    const dynamicCols = summary.categories.map((c) => ({
      title: (
        <Tooltip title={c.name}>
          <span>{c.categoryId}</span>
        </Tooltip>
      ),
      dataIndex: `cat_${c.id}`,
      key: `cat_${c.id}`,
      sorter: (a: TableRow, b: TableRow) => {
        const aCount = a[`cat_${c.id}`]?.count || 0;
        const bCount = b[`cat_${c.id}`]?.count || 0;
        return aCount - bCount;
      },
      render: (value: { count: number; ticketNumbers: string[] } | undefined, row: TableRow) => {
        const count = value?.count || 0;
        const tickets = value?.ticketNumbers || [];
        const onClick = () => {
          if (tickets.length) {
            setModalTitle(`Tiket untuk ${row.memberName} - ${c.categoryId}`);
            setModalTickets(tickets);
            setModalOpen(true);
          }
        };
        return (
          <span style={{ cursor: tickets.length ? 'pointer' : 'default', color: tickets.length ? '#1677ff' : 'inherit' }} onClick={onClick}>
            {count}
          </span>
        );
      },
    }));

    baseCols.push(...dynamicCols);
    baseCols.push({
      title: 'Total Tiket',
      dataIndex: 'totalTickets',
      key: 'totalTickets',
      sorter: (a: TableRow, b: TableRow) => a.totalTickets - b.totalTickets,
    });
    return baseCols;
  }, [summary]);

  const dataSource = useMemo(() => {
    if (!summary) return [] as TableRow[];
    return summary.members.map((m) => {
      const row: TableRow = {
        key: m.memberId,
        memberId: m.memberId,
        memberName: m.memberName,
        totalTickets: m.totalTickets,
      };
      m.totalsByCategory.forEach((t) => {
        row[`cat_${t.categoryId}`] = { count: t.count, ticketNumbers: t.ticketNumbers };
      });
      return row;
    });
  }, [summary]);

  const poolOptions = useMemo(() => {
    return (pools || []).map((p) => ({ label: p.name, value: p.id }));
  }, [pools]);

  const downloadCsv = (filename: string, rows: Array<Array<string | number>>) => {
    const escapeValue = (value: string | number) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csvContent = rows.map((row) => row.map(escapeValue).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    if (!summary) return;

    const headers = ['Nama Anggota', 'ID Anggota', ...summary.categories.map((c) => c.categoryId), 'Total Tiket'];
    const rows = summary.members.map((m) => {
      const categoryCounts = summary.categories.map((c) => {
        const match = m.totalsByCategory.find((t) => t.categoryId === c.id);
        return match ? match.count : 0;
      });
      return [m.memberName, m.memberId, ...categoryCounts, m.totalTickets];
    });

    downloadCsv('laporan-undian.csv', [headers, ...rows]);
  };

  const renderTableFooter = useMemo(
    () => (currentPageData: readonly TableRow[]) => {
      if (!summary || !dataSource.length || !summary.categories) return null;

      const totalRow = {
        memberId: 'Total',
        memberName: '',
        totalTickets: 0,
        categories: {} as Record<number, number>,
      };

      // Sum total tickets from all data (not just current page)
      let totalAllTickets = 0;
      dataSource.forEach((row) => {
        totalAllTickets += row.totalTickets as number;
      });
      totalRow.totalTickets = totalAllTickets;

      // Sum per category from all data
      if (summary?.categories) {
        summary.categories.forEach((c) => {
          const categoryTotal = dataSource.reduce((sum, row) => {
            const catData = row[`cat_${c.id}`];
            return sum + (catData?.count || 0);
          }, 0);
          totalRow.categories[c.id] = categoryTotal;
        });
      }

      return (
        <Table.Summary>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <strong>Total</strong>
            </Table.Summary.Cell>
            {summary?.categories.map((c, idx) => (
              <Table.Summary.Cell key={`footer-cat-${c.id}`} index={idx + 1}>
                <strong>{totalRow.categories[c.id] || 0}</strong>
              </Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={summary?.categories.length ? summary.categories.length + 1 : 1}>
              <strong>{totalRow.totalTickets}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      );
    },
    [summary, dataSource],
  );

  return (
    <ContentWithErrorHandler isError={error} isLoading={false}>
      <Card title="Laporan Undian" size="small">
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Text>Pilih Pool:</Text>
          <Select
            allowClear
            placeholder="Semua Pool"
            style={{ width: 280 }}
            options={poolOptions}
            value={poolId}
            onChange={(val) => setPoolId(val)}
          />
          <Button type="primary" onClick={handleDownloadCsv} disabled={!summary?.members?.length}>
            Download CSV
          </Button>
        </div>
        <Table
          size="small"
          loading={isFetching}
          columns={columns}
          dataSource={dataSource}
          style={{ width: '100%' }}
          showSorterTooltip={false}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} data`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size) setPageSize(size);
            },
            onShowSizeChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          bordered
          summary={renderTableFooter}
        />
        <Modal title={modalTitle} open={modalOpen} onCancel={() => setModalOpen(false)} footer={null}>
          {modalTickets.length ? (
            <ul>
              {modalTickets.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          ) : (
            <Text>Tidak ada tiket.</Text>
          )}
        </Modal>
      </Card>
    </ContentWithErrorHandler>
  );
};

export default MembersLotteryReport;
